/**
 * JPG format enforcer
 *
 * Rewrites every Shopify CDN image URL on the page to include format=pjpg.
 * Catches <img src>, <img srcset>, data-sih-src and similar hover-app attrs.
 *
 * Why: Shopify's CDN content-negotiates to WebP based on Accept header.
 * Default WebP quality introduces moire on fine repeating textures (velvet,
 * jacquard, silk). Forcing JPG bypasses that compression path.
 */
(function () {
  'use strict';

  const SHOPIFY_CDN = /cdn\.shopify\.com|cdn\/shop\/files/;
  const FORMAT_PARAM = 'format=pjpg';

  function rewriteUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (!SHOPIFY_CDN.test(url)) return url;
    if (url.indexOf('format=') !== -1) return url;
    const separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + FORMAT_PARAM;
  }

  function rewriteSrcset(srcset) {
    if (!srcset) return srcset;
    return srcset
      .split(',')
      .map((entry) => {
        const trimmed = entry.trim();
        const parts = trimmed.split(/\s+/);
        if (!parts.length) return trimmed;
        parts[0] = rewriteUrl(parts[0]);
        return parts.join(' ');
      })
      .join(', ');
  }

  function rewriteElement(el) {
    if (!el || el.dataset.jpgEnforced) return;

    if (el.tagName === 'IMG' || el.tagName === 'SOURCE') {
      const src = el.getAttribute('src');
      const newSrc = rewriteUrl(src);
      if (newSrc !== src) el.setAttribute('src', newSrc);

      const srcset = el.getAttribute('srcset');
      const newSrcset = rewriteSrcset(srcset);
      if (newSrcset !== srcset) el.setAttribute('srcset', newSrcset);
    }

    ['data-sih-src', 'data-hover-src', 'data-secondary-image', 'data-flip-image'].forEach((attr) => {
      const value = el.getAttribute(attr);
      const newValue = rewriteUrl(value);
      if (newValue && newValue !== value) el.setAttribute(attr, newValue);
    });

    el.dataset.jpgEnforced = 'true';
  }

  function rewriteAll(root) {
    root = root || document;
    const elements = root.querySelectorAll('img, source, [data-sih-src], [data-hover-src]');
    elements.forEach(rewriteElement);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => rewriteAll());
  } else {
    rewriteAll();
  }

  document.addEventListener('shopify:section:load', (e) => rewriteAll(e.target));

  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.tagName === 'IMG' || node.tagName === 'SOURCE') {
            rewriteElement(node);
          } else if (node.querySelectorAll) {
            rewriteAll(node);
          }
        });
        if (mutation.type === 'attributes' && mutation.target.tagName === 'IMG') {
          delete mutation.target.dataset.jpgEnforced;
          rewriteElement(mutation.target);
        }
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'data-sih-src'],
    });
  }
})();
